import * as E       from '../errors';
import { Target }   from './Target';
import { Listener } from './Listener';

export type TBubblingTypes = 'parents' | 'childs' | 'none';

export interface IEventDescriptor<
	TTarget extends TBaseTarget,
	TBaseTarget extends Target<TBaseTarget, TBaseTarget>
>
{
	target : TTarget;
	type : string;
	subType? : string | null;
	bubblingType? : TBubblingTypes;
	bubblingDirection? : 'up' | 'down' | 'down-up';//'up-down' to do later;
	bubbles? : boolean;
	cancelable? : boolean;
	composed? : boolean;
	details? : any;
	fromEvent? : Event<TBaseTarget, TBaseTarget>;
};

export enum EventPhase {
	none       = 0,
	capturing  = 1,
	atTarget   = 2,
	bubbling   = 3,
	dispatched = 4
};

export class Event<
	TTarget extends TBaseTarget,
	TBaseTarget extends Target<TBaseTarget, TBaseTarget>,
	TDetails = any
>
{
	static fromNative<TTarget extends Target<any, any> = any>(
		event : globalThis.Event,
		target : TTarget
	)
	{
		return (new Event<TTarget, TTarget>({
			target,
			type :            'native',
			subType :         event.type,
			bubbles :         event.bubbles,
			cancelable :      event.cancelable,
			composed :        event.composed,
			details :         {
				from : event
			}
		}));
	};

	targetsPath : TBaseTarget[] = [];
	eventsPath : Event<any, any, any>[];

	private fillTargetsPath() {
		if (!this.composed) {
			return;
		}
		let t : TBaseTarget | null = this.target;
		while (t) {
			this.targetsPath.push(t);
			t = t.getParent();
		}
	};

	details? : TDetails;
	readonly type : string;
	readonly subType : string | null;
	readonly bubbles : boolean;
	readonly bubblingType : TBubblingTypes;
	readonly bubblingDirection : IEventDescriptor<TTarget, TBaseTarget>['bubblingDirection'];
	readonly cancelable : boolean;
	composed : boolean;
	target : TTarget;
	currentTarget : TBaseTarget;
	phase : EventPhase;
	defaultPrevented : boolean = false;
	timeStamp : number;
	stacktrace : string;
	isPropagationStopped = false;
	isPropagationImmediatelyStopped = false;
	currentListener : TBaseTarget | null = null;
	// currentListener : Listener<TBaseTarget, TBaseTarget> | null = null;

	stopPropagation(bypassNative = false) {
		this.isPropagationStopped = true;
		if ((this.type === 'native') && !bypassNative) {
			(<globalThis.Event> (<any> this.details).from).stopPropagation();
		}
	};

	stopImmediatePropagation() {
		this.isPropagationImmediatelyStopped = true;
		this.stopPropagation(true);
		if (this.type === 'native') {
			(<globalThis.Event> (<any> this.details).from).stopImmediatePropagation();
		}
	};

	composedPath() {
		return (Array.from(this.targetsPath));
	};

	copy<
		TNewTarget extends TBaseTarget = any
	>(target : TNewTarget) : Event<TNewTarget, TBaseTarget> {
		return (new Event<TNewTarget, TBaseTarget>({
			target,
			type :            this.type,
			subType :         this.subType,
			bubbles :         this.bubbles,
			cancelable :      this.cancelable,
			composed :        this.composed,
			details :         Object.assign({}, this.details) // if details contains references, they must be manually copied
		}));
	};

	/**
	 * Propagate event to another tree or graph
	 **/
	async propagate<
		TOutTarget extends TOutBaseTarget,
		TOutBaseTarget extends Target<TOutBaseTarget, TOutBaseTarget>
	>(
		target : TOutTarget,
		desc : Partial<Omit<IEventDescriptor<TOutTarget, TOutBaseTarget>, 'target' | 'fromEvent'>> = {}
	)
	{
		const e = new Event<TOutTarget, TOutBaseTarget>({
			target,
			type :            this.type,
			subType :         desc.subType || this.subType,
			bubbles :         (desc.bubbles !== undefined) ? desc.bubbles : this.bubbles,
			cancelable :      (desc.cancelable !== undefined) ? desc.cancelable : this.cancelable,
			composed :        (desc.composed !== undefined) ? desc.composed : this.composed,
			details :         (desc.details !== undefined) ? desc.details : this.details
		});
		await target.dispatchEvent(e);
	};

	// preventDefault() TODO
	constructor(desc : IEventDescriptor<TTarget, TBaseTarget>) {
		this.target = desc.target;
		this.currentTarget = this.target;
		this.phase = EventPhase.none;
		this.timeStamp = (new Date).getTime();
		this.type = desc.type;
		this.bubbles = desc.bubbles || true;
		this.bubblingType = desc.bubblingType || 'parents';
		this.bubblingDirection = desc.bubblingDirection || 'down-up';
		this.cancelable = desc.cancelable || true;
		this.subType = desc.subType || null;
		this.stacktrace = <string> (new Error).stack;
		if ((desc.bubblingType === 'childs') && (desc.composed)) {
			throw E.user("Event cannot be composed if 'childs' bubblingType is used", {
				descriptor : desc
			});
		}
		this.composed = desc.composed || ((desc.bubblingType === 'childs') ? false : true);
		if (desc.details !== undefined) {
			this.details = desc.details;
		}
		this.fillTargetsPath();
		this.eventsPath = !desc.fromEvent ? [] : [
			desc.fromEvent,
			...desc.fromEvent.eventsPath
		];
	};
};

export interface BoundEvent<
	TTarget extends TBaseTarget,
	TBaseTarget extends Target<TBaseTarget, TBaseTarget>
> extends Event<TTarget, TBaseTarget> {
	listener : Listener<TTarget, TBaseTarget>;
};
